<template>
  <div class="relative" ref="bellContainer">
    <button
      class="relative flex items-center justify-center w-8 h-8 rounded transition-colors hover:bg-(--p-background-100) cursor-pointer"
      @click="toggleDropdown"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="w-5 h-5 text-color"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      <span
        v-if="notificationsStore.unseenCount > 0"
        class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-4.5 h-4.5 rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
      >
        {{ notificationsStore.unseenCount > 99 ? '99+' : notificationsStore.unseenCount }}
      </span>
    </button>

    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="absolute top-full right-0 mt-2 w-80 max-h-96 bg-(--p-background-0) rounded-lg shadow-lg border border-background-200 z-50 flex flex-col overflow-hidden"
      >
        <div class="flex items-center justify-between p-3 border-b border-background-200">
          <span class="font-semibold text-sm text-color">Notificaties</span>
          <button
            v-if="notificationsStore.unseenCount > 0"
            class="text-xs text-primary-500 hover:text-primary-700 cursor-pointer"
            @click="notificationsStore.markAllSeen()"
          >
            Alles gelezen
          </button>
        </div>

        <div class="overflow-y-auto flex-1">
          <div
            v-if="notificationsStore.loading && notificationsStore.notifications.length === 0"
            class="p-4 text-center text-sm text-background-500"
          >
            Laden...
          </div>
          <div
            v-else-if="notificationsStore.notifications.length === 0"
            class="p-4 text-center text-sm text-background-500"
          >
            Geen notificaties
          </div>
          <div
            v-for="notification in notificationsStore.notifications"
            :key="notification.id"
            class="flex gap-3 p-3 cursor-pointer transition-colors hover:bg-(--p-background-50) border-b border-background-100 last:border-b-0"
            :class="{ 'bg-(--p-background-50)': !notification.seen }"
            @click="handleNotificationClick(notification)"
          >
            <div
              class="mt-1 w-2 h-2 rounded-full shrink-0"
              :class="notification.seen ? 'bg-transparent' : 'bg-primary-500'"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-color truncate">{{ notification.title }}</div>
              <div v-if="notification.body" class="text-xs text-background-500 mt-0.5 line-clamp-2">
                {{ notification.body }}
              </div>
              <div class="text-[10px] text-background-400 mt-1">
                {{ formatRelativeTime(notification.created_at) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useNotificationsStore } from '../stores/notifications'
  import type { AppNotification } from '../../../../types/shared/common'

  const notificationsStore = useNotificationsStore()
  const router = useRouter()
  const isOpen = ref(false)
  const bellContainer = ref<HTMLElement | null>(null)

  function toggleDropdown() {
    isOpen.value = !isOpen.value
  }

  function handleNotificationClick(notification: AppNotification) {
    notificationsStore.markAsSeen(notification.id)
    const link = notificationsStore.getDeepLink(notification)
    isOpen.value = false
    router.push(link)
  }

  function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'Zojuist'
    if (diffMins < 60) return `${diffMins} min geleden`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} uur geleden`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? 'en' : ''} geleden`
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (isOpen.value && bellContainer.value && !bellContainer.value.contains(event.target as Node)) {
      isOpen.value = false
    }
  }

  onMounted(() => {
    notificationsStore.init()
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<style scoped>
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.2s ease;
    transform-origin: top right;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
  }
</style>
